import { ContentType } from "@brookbush/database";
import type { CoursePreview } from "@brookbush/types";

import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";

import BookmarkButton from "~/components/BookmarkButton";
import ShareButton from "~/components/ShareButton";
import usePassedCourses from "~/hooks/usePassedCourses";

interface LandingPageCoursesCardProps extends CoursePreview {
	isFirstRecommended?: boolean;
}

const LandingPageCoursesCard = ({
	slug,
	title,
	thumbnail,
	_id,
	credits,
	appliedCoreCredits,
	appliedElectiveCredits,
	isExternalElective,
	isFirstRecommended,
}: LandingPageCoursesCardProps) => {
	const { passedCourses } = usePassedCourses();

	const isPassed = passedCourses?.some((course) => course.courseId === _id);

	return (
		<div
			className={clsx(
				"course-card group relative mx-auto flex aspect-[7/3] max-h-52 w-full cursor-pointer flex-col overflow-hidden rounded-2xl border transition-all duration-500 sm:aspect-[4/3] sm:max-w-full sm:hover:scale-105 sm:hover:shadow-slate-300 dark:shadow-slate-900 dark:hover:shadow-black/70",
				{
					"animate-pulse": isFirstRecommended,
				},
			)}
		>
			<div className="relative h-full w-full shrink-0 transition-all duration-700 sm:group-hover:scale-[1.15]">
				<Image
					src={thumbnail?.url || "/images/placeholder.png"}
					fill
					placeholder="empty"
					className="object-cover"
					alt="picture"
				/>
			</div>

			<p
				className={clsx(
					"absolute top-3.5 left-3 z-10 rounded-full bg-slate-100 px-2.5 py-1 font-medium text-base text-slate-700 shadow-xl dark:text-slate-800",
				)}
			>
				{credits} {credits === 1 ? "Credit" : "Credits"}
			</p>

			<div className="absolute top-3 right-3 z-10 flex gap-2">
				<BookmarkButton
					id="course-bookmark-btn"
					contentId={_id}
					contentType={ContentType.COURSE}
					isIconButton
				/>
				<ShareButton
					isIconButton
					id="course-share-btn"
					href={`/courses/${slug}`}
				/>
			</div>

			<Link href={`/courses/${slug}`}>
				<div
					className={clsx(
						"absolute top-0 left-0 flex h-full w-full flex-col items-start justify-end gap-2.5 bg-gradient-to-t to-transparent px-4 py-3",
						isFirstRecommended
							? "from-yellow-950"
							: isPassed
								? "from-emerald-950"
								: `from-black/90`,
					)}
				>
					<h3
						className={clsx(
							`line-clamp z-10 font-semibold text-white`,
							`text-xl 2xl:text-2xl`,
						)}
					>
						{title}
					</h3>
					{isFirstRecommended && (
						<div className="mb-1 rounded-lg bg-white px-2.5 py-0.5 font-medium dark:text-black">
							🚀 Recommended Course
						</div>
					)}

					{isPassed && (
						<div className="-m-1 mt-0 -mb-0.5 flex gap-2 text-[0.85rem]">
							<div className="mb-0.5 rounded-lg bg-emerald-300 px-2 py-0.5 font-medium dark:text-black">
								Passed
							</div>

							{appliedCoreCredits && (
								<div className="mb-0.5 whitespace-nowrap rounded-lg bg-sky-200 px-2 py-0.5 font-medium dark:text-black">
									{appliedCoreCredits} Core
								</div>
							)}

							{appliedElectiveCredits && (
								<div className="mb-0.5 whitespace-nowrap rounded-lg bg-purple-200 px-2 py-0.5 font-medium dark:text-black">
									{appliedElectiveCredits} Elective
								</div>
							)}

							{isExternalElective && (
								<div className="mb-0.5 whitespace-nowrap rounded-lg bg-amber-200 px-2 py-0.5 font-medium dark:text-black">
									External
								</div>
							)}
						</div>
					)}
				</div>
			</Link>
		</div>
	);
};

export default LandingPageCoursesCard;
